import { ReactNode, useState } from "react";
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, Plus, Trash2 } from "lucide-react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import { cn } from "~/lib/utils";
import { TabHeader } from "~/pages/manifold-nextgen/components/TabHeader";
import { Agent, AgentScope, agentPath, UpdateAgentInput, updateAgent } from "~/services/agents";

const NEW_ID = "__new__";

type CollectionField = "starter_prompts" | "featured_conversations";

export interface SortableItem {
  id: string;
}

export interface EditFormRenderProps<TInput> {
  initial: TInput;
  disabled: boolean;
  onSave: (values: TInput) => void;
  onCancel: () => void;
}

interface AgentSortableListTabProps<T extends SortableItem, TInput> {
  title: string;
  context: string;
  description: ReactNode;
  agent: Agent;
  field: CollectionField;
  items: T[];
  maxItems: number;
  canEdit: boolean;
  toInput: (item: T) => TInput;
  emptyDraft: TInput;
  renderView: (item: T) => ReactNode;
  renderEditForm: (props: EditFormRenderProps<TInput>) => ReactNode;
  addLabel: string;
  emptyMessage: string;
  errorMessage: string;
}

export function AgentSortableListTab<T extends SortableItem, TInput>({
  title,
  context,
  description,
  agent,
  field,
  items,
  maxItems,
  canEdit,
  toInput,
  emptyDraft,
  renderView,
  renderEditForm,
  addLabel,
  emptyMessage,
  errorMessage,
}: AgentSortableListTabProps<T, TInput>) {
  const { showError } = useToast();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const persist = async (next: TInput[]): Promise<boolean> => {
    setIsSaving(true);
    const body = { [field]: next } as unknown as UpdateAgentInput;
    try {
      const updated = await updateAgent(agent.id, body);
      await mutate(agentPath(agent.id), updated, { revalidate: false });
      return true;
    } catch {
      // Restore canonical server state in case an optimistic update painted ahead.
      await mutate(agentPath(agent.id));
      showError(errorMessage);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEdit = async (id: string, values: TInput) => {
    const next =
      id === NEW_ID
        ? [...items.map(toInput), values]
        : items.map((it) => (it.id === id ? { ...it, ...values } : it)).map(toInput);
    const ok = await persist(next);
    if (ok) setEditingId(null);
  };

  const handleDelete = async (id: string) => {
    const next = items.filter((it) => it.id !== id).map(toInput);
    await persist(next);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((it) => it.id === active.id);
    const newIndex = items.findIndex((it) => it.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(items, oldIndex, newIndex);
    // Paint the reorder immediately by writing to the SWR cache.
    const optimistic = { ...agent, [field]: reordered } as Agent;
    await mutate(agentPath(agent.id), optimistic, { revalidate: false });
    await persist(reordered.map(toInput));
  };

  const remaining = maxItems - items.length;
  const isAdding = editingId === NEW_ID;
  const canAdd = canEdit && remaining > 0 && editingId === null;
  const isEmpty = items.length === 0 && !isAdding;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <TabHeader
        title={title}
        isSystemAgent={agent.scope === AgentScope.SYSTEM}
        count={items.length}
        max={maxItems}
        context={context}
        description={description}
      />

      {isEmpty ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
          {emptyMessage}
        </div>
      ) : (
        <div className="flex flex-col divide-y divide-border rounded-lg border border-border">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items.map((it) => it.id)}
              strategy={verticalListSortingStrategy}
            >
              {items.map((item) =>
                editingId === item.id ? (
                  <EditRow key={item.id}>
                    {renderEditForm({
                      initial: toInput(item),
                      disabled: isSaving,
                      onSave: (values) => handleSaveEdit(item.id, values),
                      onCancel: () => setEditingId(null),
                    })}
                  </EditRow>
                ) : (
                  <ViewRow
                    key={item.id}
                    id={item.id}
                    canEdit={canEdit}
                    disabled={editingId !== null || isSaving}
                    onEdit={() => setEditingId(item.id)}
                    onDelete={() => handleDelete(item.id)}
                  >
                    {renderView(item)}
                  </ViewRow>
                )
              )}
            </SortableContext>
          </DndContext>
          {isAdding && (
            <EditRow>
              {renderEditForm({
                initial: emptyDraft,
                disabled: isSaving,
                onSave: (values) => handleSaveEdit(NEW_ID, values),
                onCancel: () => setEditingId(null),
              })}
            </EditRow>
          )}
        </div>
      )}

      {canEdit && (
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            disabled={!canAdd}
            onClick={() => setEditingId(NEW_ID)}
            className="text-primary hover:text-primary/80 disabled:text-muted-foreground disabled:opacity-100"
          >
            <Plus />
            {addLabel}
          </Button>
          <span className="text-xs text-muted-foreground">{remaining} remaining</span>
        </div>
      )}
    </div>
  );
}

interface ViewRowProps {
  id: string;
  canEdit: boolean;
  disabled: boolean;
  onEdit: () => void;
  onDelete: () => void;
  children: ReactNode;
}

const ViewRow = ({ id, canEdit, disabled, onEdit, onDelete, children }: ViewRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !canEdit || disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "group flex items-center gap-3 px-3 py-3",
        "first:rounded-t-lg last:rounded-b-lg",
        "hover:bg-accent/30"
      )}
    >
      <button
        type="button"
        aria-label="Drag to reorder"
        disabled={!canEdit || disabled}
        className={cn(
          "shrink-0 cursor-grab text-muted-foreground/60",
          "hover:text-muted-foreground focus-visible:outline-none",
          "active:cursor-grabbing disabled:cursor-default disabled:opacity-30"
        )}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-4" />
      </button>

      <div className="min-w-0 flex-1">{children}</div>

      {canEdit && (
        <div
          className={cn(
            "flex shrink-0 items-center gap-1",
            "opacity-0 transition-opacity group-hover:opacity-100",
            "focus-within:opacity-100"
          )}
        >
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label="Edit"
            disabled={disabled}
            onClick={onEdit}
          >
            <Pencil className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-7 text-muted-foreground hover:text-destructive"
            aria-label="Delete"
            disabled={disabled}
            onClick={onDelete}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

interface EditRowProps {
  children: ReactNode;
}

const EditRow = ({ children }: EditRowProps) => (
  <div className="flex flex-col gap-3 px-4 py-4 first:rounded-t-lg last:rounded-b-lg">
    {children}
  </div>
);

interface EditFormFooterProps {
  counterCurrent: number;
  counterMax: number;
  canSave: boolean;
  disabled: boolean;
  onCancel: () => void;
  onSave: () => void;
}

export const EditFormFooter = ({
  counterCurrent,
  counterMax,
  canSave,
  disabled,
  onCancel,
  onSave,
}: EditFormFooterProps) => (
  <div className="flex items-center justify-between">
    <span className="text-xs text-muted-foreground">
      {counterCurrent} / {counterMax}
    </span>
    <div className="flex items-center gap-2">
      <Button variant="ghost" size="sm" onClick={onCancel} disabled={disabled}>
        Cancel
      </Button>
      <Button size="sm" onClick={onSave} disabled={!canSave} loading={disabled}>
        Save
      </Button>
    </div>
  </div>
);
